import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { saFetch, SALoading, fmtDateTime, fmtMoney, inputCls, btnOutline, btnPrimary, PayBadge } from "./saCommon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, CheckCircle, CircleNotch, Money } from "@phosphor-icons/react";

const EMPTY = { tenant_id: "", plan_id: "", billing_cycle: "monthly", amount: "", method: "transfer", note: "", mark_approved: true };
const METHOD_LABELS = { paypal: "PayPal", transfer: "Transferencia", cash: "Efectivo" };

export default function SAPagos() {
  const [payments, setPayments] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    saFetch("/superadmin/payments").then(setPayments).catch((e) => toast.error(e.message));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    saFetch("/superadmin/companies-v2?per_page=200").then((d) => setCompanies(d.items || [])).catch(() => {});
    saFetch("/superadmin/plans").then(setPlans).catch(() => {});
  }, []);

  const verify = async (p) => {
    try {
      await saFetch(`/superadmin/payments/${p.id}/verify`, { method: "POST" });
      toast.success("Pago verificado y suscripción activada");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const setStatus = async (p, status) => {
    try {
      await saFetch(`/superadmin/payments/${p.id}/status`, { method: "POST", body: JSON.stringify({ status }) });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saFetch("/superadmin/payments/manual", {
        method: "POST",
        body: JSON.stringify({ ...form, amount: Number(form.amount) || 0, plan_id: form.plan_id || "" }),
      });
      toast.success(form.mark_approved ? "Pago registrado y suscripción activada" : "Pago registrado como pendiente");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!payments) return <SALoading label="Cargando pagos..." />;

  const filtered = filter === "all" ? payments : payments.filter((p) => p.status === filter);

  return (
    <div data-testid="sa-pagos">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Pagos</h1>
          <p className="text-sm text-muted-foreground mt-1">{payments.length} pago(s) registrados</p>
        </div>
        <Button className={btnPrimary} data-testid="sa-new-payment" onClick={() => setOpen(true)}>
          <Plus className="mr-2" size={15} /> Registrar pago manual
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[["all", "Todos"], ["pending", "Pendientes"], ["approved", "Aprobados"], ["rejected", "Rechazados"], ["refunded", "Reembolsados"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`sa-pay-filter-${k}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors duration-200 ${filter === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-accent/60"}`}>
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-border rounded-sm p-10 text-center text-muted-foreground bg-card" data-testid="sa-payments-empty">
          <Money size={28} className="mx-auto mb-3" />
          Sin pagos en este estado.
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card">
          <table className="w-full text-sm min-w-[900px]" data-testid="sa-payments-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 font-normal">Fecha</th>
                <th className="p-3 font-normal">Empresa</th>
                <th className="p-3 font-normal">Concepto</th>
                <th className="p-3 font-normal">Método</th>
                <th className="p-3 font-normal">Total</th>
                <th className="p-3 font-normal">Estado</th>
                <th className="p-3 font-normal text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`sa-payment-row-${p.id}`}>
                  <td className="p-3 text-foreground/80 text-xs">{fmtDateTime(p.paid_at || p.created_at)}</td>
                  <td className="p-3 text-foreground font-medium">{p.company_name || p.tenant_id.slice(0, 8)}</td>
                  <td className="p-3">
                    <p className="text-foreground/80">{p.plan_name || "—"} · {p.billing_cycle === "annual" ? "Anual" : "Mensual"}</p>
                    <p className="text-[11px] text-muted-foreground font-mono">{p.transaction_id || p.paypal_order_id || ""}</p>
                  </td>
                  <td className="p-3 text-foreground/80">{METHOD_LABELS[p.method] || p.method}</td>
                  <td className="p-3 text-foreground font-medium">{fmtMoney(p.total, p.currency)}</td>
                  <td className="p-3"><PayBadge status={p.status} /></td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1 items-center">
                      {p.status === "pending" && (
                        <Button variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-950/30" data-testid={`sa-payment-verify-${p.id}`} onClick={() => verify(p)}>
                          <CheckCircle className="mr-1" size={14} /> Verificar
                        </Button>
                      )}
                      <Select value={p.status} onValueChange={(v) => setStatus(p, v)}>
                        <SelectTrigger className={`${inputCls} h-8 w-32`} data-testid={`sa-payment-status-${p.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          <SelectItem value="pending">Pendiente</SelectItem>
                          <SelectItem value="approved">Aprobado</SelectItem>
                          <SelectItem value="rejected">Rechazado</SelectItem>
                          <SelectItem value="cancelled">Cancelado</SelectItem>
                          <SelectItem value="refunded">Reembolsado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Registrar pago manual</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="sa-payment-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empresa *</Label>
              <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-payment-company"><SelectValue placeholder="Selecciona la empresa" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {companies.map((c) => <SelectItem key={c.tenant_id} value={c.tenant_id}>{c.name || c.tenant_id.slice(0, 8)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Plan</Label>
                <Select value={form.plan_id || "none"} onValueChange={(v) => setForm({ ...form, plan_id: v === "none" ? "" : v })}>
                  <SelectTrigger className={inputCls} data-testid="sa-payment-plan"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="none">Sin plan</SelectItem>
                    {plans.map((pl) => <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Ciclo</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger className={inputCls} data-testid="sa-payment-cycle"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Monto *</Label>
                <Input type="number" step="0.01" min="0" required className={inputCls} data-testid="sa-payment-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Método</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className={inputCls} data-testid="sa-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="transfer">Transferencia</SelectItem>
                    <SelectItem value="cash">Efectivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nota</Label>
              <Textarea className={`${inputCls} min-h-[60px]`} data-testid="sa-payment-note" placeholder="Referencia, banco, observaciones..." value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <Switch checked={form.mark_approved} onCheckedChange={(v) => setForm({ ...form, mark_approved: v })} data-testid="sa-payment-approve" />
              Marcar como aprobado (activa/renueva la suscripción de inmediato)
            </label>
            <DialogFooter>
              <Button type="submit" disabled={saving || !form.tenant_id} className={btnPrimary} data-testid="sa-payment-save">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Registrar pago
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
